/* eslint-disable react/jsx-no-target-blank */ /* NTCONT-737 customer request. */
import React, { useEffect, useState } from 'react';
import propTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { getDataFromApi, ROOT } from '../../../lib/data-service';
import gtmEvent from '../../../lib/gtmEvents'
import { enforceProtocol, formatDistance } from '../../../lib/utils';

import { useConfig, useBuyingOptions, useMap, useDeviceType } from '../../../hooks';

import Address from './Address';
import PopoverButton from '../../PopoverButton';
import DealerPurchaseSection from './DealerPurchaseSection';
import Heading from '../../Heading';

const extraDealerFields = ['footerBanner', 'products'];

/**
 * Dealer component.
 *
 * @param {object} param0
 * @param {Dealer} param0.dealer Dealer object.
 * @param {GeocodeResult} param0.userLocation User location.
 * @param {boolean} param0.isFromUrl Is the component being rendered from a URL.
 * @param {function} param0.onActiveDealerChange Callback to be called when the active dealer changes.
 */
function Dealer({ dealer, userLocation, isFromUrl, onActiveDealerChange }) {
  const { t } = useTranslation('dealers');
  const { config } = useConfig();
  const { isMobile } = useDeviceType();
  const [dealerExtra, setDealerExtra] = useState(null);
  const { mapEnabled, setMapVisible } = useMap();
  const buyingOptionsProduct = useBuyingOptions();
  const [loadingExtra, setLoadingExtra] = useState(true);

  const isHeavy = config.isHeavy;
  const productCode = !isHeavy ? buyingOptionsProduct?.selectedCode : null;
  const footerBanner = dealer.footerBanner || dealerExtra?.footerBanner || null;
  const dealerProduct = isHeavy ? null : dealerExtra?.products?.find(item => item.productCode === productCode);
  const loadingProductAvailability = !isHeavy && dealer.productDataEnabled && !!productCode && loadingExtra;

  // Trigger analytics event when dealer is viewed
  useEffect(() => {
    gtmEvent({
      'event': 'view_dealer',
      'dealer_name': dealer?.company_name
    });
  }, [dealer]);

  // Fetch extra data for single dealer
  useEffect(() => {
    getDataFromApi(`dealers/data/${dealer.slug}/` + (productCode ? `?productCode=${productCode}` : '')).then(response => {
      const singleDealer = response.body?.result?.dealer;
      if (singleDealer) {
        const extra = {};
        extraDealerFields.forEach(field => {
          extra[field] = singleDealer[field] || dealer[field] || null;
        });
        setDealerExtra(extra);
        setLoadingExtra(false);
        console.log({ dealer, singleDealer, extra });
      }
    });
  }, [dealer, productCode]);

  // Street view
  let streetViewUrl = false;

  if (!dealer.noStreetView) {
    const streetviewUrlParts = [
      'https://maps.googleapis.com/maps/api/streetview?size=640x300',
      `key=${window.ntGKey || 'AIzaSyDaA6JQZgAw6obo_lZMDR1ok60FSNKunkQ'}`,
      '&location='
    ].join('&');
    const location = [
      dealer.streetaddress,
      dealer.zipcode,
      dealer.city,
      dealer.state
    ].join(', ');

    streetViewUrl = streetviewUrlParts + encodeURIComponent(location);
  }

  if (dealer.streetview_override) {
    streetViewUrl = dealer.streetview_override;
  }

  // Google Directions
  let directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${dealer.lat},${dealer.long}`;

  if (userLocation) {
    directionsUrl = `https://www.google.com/maps/dir/?api=1&origin=${userLocation.lat},${userLocation.lng}&destination=${dealer.lat},${dealer.long}`;
  }

  let distance = formatDistance(dealer.distance, config.distanceUnit);
  let distanceText = t('Distance');

  // Use route length if route is available
  const directions = {};
  if (directions?.routes?.length > 0) {
    const routeLength = directions.routes.reduce((value, route) => {
      return value + route.legs.reduce((legValue, leg) => {
        return legValue + leg.distance.value;
      }, 0);
    }, 0);

    if (routeLength > 0) {
      distance = formatDistance(routeLength / 1000, config.distanceUnit);
      distanceText = t('Route length');
    }
  }

  //NTCONT-976 Japan wants different address formatting
  const isJp = document.body.dataset.language === 'jp_JP';

  const dealerTitle = (
    <>
      <span>
        {config.reorderCompanyName && dealer.nameReorderable
          ?
          config.nameFields.map(field => dealer[field]).reverse().join(' ')
          :
          config.nameFields.map(field => dealer[field]).join(' ')
        }
      </span>
      {/* {!isJp ? config.addressFields
        .filter(field => dealer[field] !== '')
        .map((field, _) => {
          if (field in dealer && dealer[field] !== '') {
            return (
              <React.Fragment key={'dealer-field-' + field}>
                {', '}
                <span
                  className={field === 'streetAddress' ? 'inlineblock' : ''}
                >
                  {dealer[field]}
                </span>
              </React.Fragment>
            );
          }

          return null;
        }) : null} */}
    </>
  );

  const isNad = !!dealer.company_types.find(ct => ct.type === 'nokian');
  const heavyProductGroups = !isHeavy || !dealer.heavy_product_groups || dealer.heavy_product_groups.length === 0 ? false : dealer.heavy_product_groups.map(group => (
    <li key={group.link}><a href={group.link}>{group.name}</a></li>
  ));

  const companyTypeIcons = dealer.company_types.filter(ct => !!ct.icon && ct.description).map(ct => (
    <PopoverButton key={'ct-' + ct.id} icon={ct.icon} description={ct.description}></PopoverButton>
  ));

  const phoneNumbers = !dealer.phoneNumbers ? dealer.telephone : dealer.phoneNumbers.map((number, i) => {
    const link = !number.value ? number.label : <a
      className="link"
      target="_blank" // eslint-disable-line
      rel="noreferrer noopener nofollow"
      href={'tel:' + number.value}
    >
      {number.label}
    </a>;

    return <span key={i}>
      {i > 0 ? ', ' : ''}
      {link}
    </span>;
  });

  function handleClose(e) {
    e.preventDefault();
    onActiveDealerChange(null);

    gtmEvent({
      'event': 'view_dealer_list',
      'dealer_list_open_method': 'Back to dealers list'
    });
  }

  return (
    <article className="single-dealer" itemScope={true} itemType="http://schema.org/LocalBusiness">
      {streetViewUrl && (
        <div
          className="streetview"
          style={{ backgroundImage: `url(${streetViewUrl})` }}
        ></div>
      )}

      <a href={ROOT} className="back-to-list link-wrapper" data-back-to-list onClick={handleClose}>
        &lsaquo;&nbsp;
        <span className="link">{t('Back to dealers list')}</span>
      </a>

      <div className="single-dealer__details">

        <Heading level={isFromUrl ? 1 : 2} className="single-dealer__name h4">{dealerTitle}</Heading>

        {isNad && (
          <p className="single-dealer__nad h5">{t('Nokian tyres authorized dealer')}</p>
        )}

        <p
          className="single-dealer__address"
          itemProp="address"
          itemScope
          itemType="http://schema.org/PostalAddress"
        >
          <Address {...dealer} />
        </p>

        {companyTypeIcons.length > 0 && (
          <div className="company-types">{companyTypeIcons}</div>
        )}

        {distance && userLocation && (
          <p className="single-dealer__distance">
            {distanceText} {distance} {t('du')}
          </p>
        )}

        {directionsUrl && (
          <p>
            <a
              target="_blank"
              rel="noopener noreferrer nofollow"
              href={directionsUrl}
              className={`a--external single-dealer-directions-button`}
            >
              {t('Show driving directions')}
            </a>
          </p>
        )}

        <DealerPurchaseSection
          dealer={dealer}
          product={dealerProduct}
          buyingOptionsProduct={!isHeavy ? buyingOptionsProduct : null}
          loading={loadingProductAvailability}
          headingLevel={isFromUrl ? 2 : 3}
        />

        {heavyProductGroups && (
          <>
            <Heading level={isFromUrl ? 2 : 3} className="h6">{t('Selling products in categories')}</Heading>
            <ul className="product-groups">{heavyProductGroups}</ul>
          </>
        )}

        {!dealerProduct && !isHeavy && (
          <Heading level={isFromUrl ? 2 : 3} className="single-dealer__contact-heading h6">{t('Contact dealer for price and availability')}</Heading>
        )}

        {phoneNumbers && (
          <>
            <Heading level={isFromUrl ? 2 : 3} className="h7">{t('Tel')}</Heading>
            <p>
              {phoneNumbers}
            </p>
          </>
        )}

        {dealer.website_url && (
          <>
            <Heading level={isFromUrl ? 2 : 3} className="h7">{t('Website')}</Heading>
            <p>
              <a
                className="a--external link"
                target="_blank" // eslint-disable-line
                rel="noopener" // eslint-disable-line
                href={enforceProtocol(dealer.website_url)}
              >
                {dealer.website_url}
              </a>
            </p>
          </>
        )}

        {(config.includeServices || config.showServices && dealer.services_data.length > 0) && (
          <>
            <Heading level={isFromUrl ? 2 : 3} className="h6">{t('Services')}</Heading>
            <ul className="dealer-services-list">
              {dealer.services_data.map(service => (
                <li key={service.id}>{service.name}</li>
              ))}
            </ul>
          </>
        )}

        {dealer.description && (
          <>
            <Heading level={isFromUrl ? 2 : 3} className="h6">{t('About')}</Heading>
            <div className="dealer-description" dangerouslySetInnerHTML={{ __html: dealer.description }}></div>
          </>
        )}

        {footerBanner && (
          <div className="dealer-footer" dangerouslySetInnerHTML={{ __html: footerBanner }}></div>
        )}
      </div>
    </article>
  )
}

Dealer.propTypes = {
  dealer: propTypes.object,
  userLocation: propTypes.object,
  isFromUrl: propTypes.bool,
  onActiveDealerChange: propTypes.func,
};

export default Dealer;
